// hooks/useTokenPrice.js
import { useState, useEffect } from 'react';

// Cache object to store token prices
// Structure: { "tokenAddress-chainId": { price: number, timestamp: number } }
const priceCache = {};

// Cache duration: 10 minutes in milliseconds
const CACHE_DURATION = 10 * 60 * 1000;

const useTokenPrice = (tokenAddress, chainId = 1) => {
    const [price, setPrice] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Map chain IDs to CoinGecko platform names
    const chainToPlatform = {
        1: 'ethereum',
        56: 'binance-smart-chain',
        137: 'polygon-pos',
        42161: 'arbitrum-one',
        43114: 'avalanche',
        10: 'optimistic-ethereum',
        250: 'fantom',
        8453: 'base',
    };

    // Native token addresses (0x0 or null means native token)
    const nativeTokenIds = {
        1: 'ethereum',           // ETH
        56: 'binancecoin',       // BNB
        137: 'matic-network',    // MATIC
        42161: 'ethereum',       // ETH on Arbitrum
        43114: 'avalanche-2',    // AVAX
        10: 'ethereum',          // ETH on Optimism
        250: 'fantom',           // FTM
        8453: 'ethereum',        // ETH on Base
    };

    useEffect(() => {
        // If no token address provided, skip fetching
        if (!tokenAddress) {
            setPrice(null);
            setLoading(false);
            return;
        }

        const fetchTokenPrice = async () => {
            // Create a unique cache key for this token and chain
            const normalizedAddress = tokenAddress?.toLowerCase() || 'native';
            const cacheKey = `${normalizedAddress}-${chainId}`;

            // Check if we have a valid cached price
            const cachedData = priceCache[cacheKey];
            const now = Date.now();

            if (cachedData && (now - cachedData.timestamp) < CACHE_DURATION) {
                // Use cached price if it's less than 10 minutes old
                // console.log(`Using cached price for ${cacheKey}`);
                setPrice(cachedData.price);
                setLoading(false);
                return;
            }

            // If no valid cache, fetch from API
            setLoading(true);
            setError(null);

            try {
                const platform = chainToPlatform[chainId];

                if (!platform) {
                    throw new Error(`Unsupported chain ID: ${chainId}`);
                }

                // Check if it's a native token (no address, 0x0, or 0x0000...)
                const isNativeToken = !tokenAddress ||
                    tokenAddress === '0x0' ||
                    tokenAddress === '0x0000000000000000000000000000000000000000';

                let response;

                if (isNativeToken) {
                    // Use simple/price endpoint for native tokens
                    const tokenId = nativeTokenIds[chainId];
                    // console.log(`Fetching price for native token: ${tokenId}`);

                    response = await fetch(
                        `https://pro-api.coingecko.com/api/v3/simple/price?ids=${tokenId}&vs_currencies=usd`,
                        {
                            headers: {
                                'x-cg-pro-api-key': process.env.REACT_APP_COINGECKO_API_KEY || 'CG-AMT6ozdfd8K7Qpx7qESamW9hKZu',
                            },
                        }
                    );

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const data = await response.json();
                    const priceData = data[tokenId]?.usd;

                    if (priceData === undefined) {
                        throw new Error('Token price not found');
                    }

                    // Cache the price
                    priceCache[cacheKey] = {
                        price: priceData,
                        timestamp: Date.now()
                    };

                    setPrice(priceData);
                } else {
                    // Use token_price endpoint for ERC20 tokens
                    // console.log(`Fetching price for ${normalizedAddress} on ${platform}`);

                    response = await fetch(
                        `https://pro-api.coingecko.com/api/v3/simple/token_price/${platform}?contract_addresses=${normalizedAddress}&vs_currencies=usd`,
                        {
                            headers: {
                                'x-cg-pro-api-key': process.env.REACT_APP_COINGECKO_API_KEY || 'CG-AMT6ozdfd8K7Qpx7qESamW9hKZu',
                            },
                        }
                    );

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const data = await response.json();
                    const priceData = data[normalizedAddress]?.usd;

                    if (priceData === undefined) {
                        throw new Error('Token price not found');
                    }

                    // Cache the price
                    priceCache[cacheKey] = {
                        price: priceData,
                        timestamp: Date.now()
                    };

                    setPrice(priceData);
                }
            } catch (err) {
                console.error('Error fetching token price:', err);
                setError(err.message);
                setPrice(null);
            } finally {
                setLoading(false);
            }
        };

        fetchTokenPrice();

        // Optional: Auto-refresh every 30 seconds
        // const interval = setInterval(fetchTokenPrice, 30000);
        // return () => clearInterval(interval);
    }, [tokenAddress, chainId]);

    return { price, loading, error };
};

export { useTokenPrice };